import { Button, ButtonDesign } from '@ui5/webcomponents-react';
import { ResultUserMessage, UserMessage } from 'components/shared/UserMessage';
import { Distribution, DistributionStatus, Status, User } from 'generated/graphql';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './CompleteQuery.jss';
import { CompleteQueryStrategy } from './CompleteQueryStrategy/CompleteQueryStrategy';

type UserProps = Pick<User, 'internal' | 'suppliers'> | null;

export type CompleteQueryProps = {
  id: string;
  status?: Status;
  user?: UserProps;
  distributions?: Distribution[];
};

export type UserDialogProps = {
  open: boolean;
  closeConfirmationDialog: () => void;
  setResultMessage: (resultMessage: ResultUserMessage) => void;
} & Pick<CompleteQueryProps, 'id' | 'user' | 'distributions'>;

export const CompleteQuery: React.FC<CompleteQueryProps> = (props: CompleteQueryProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id, user, status, distributions } = props;

  const [isConfirmationBoxOpen, setIsConfirmationBoxOpen] = useState(false);
  const [isUserMessageOpen, setIsUserMessageOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<ResultUserMessage>({
    isSuccess: false,
    message: t('display-query-complete-query-error')
  });

  const setUserMessage = (message: ResultUserMessage) => {
    setResultMessage(message);
    setIsUserMessageOpen(true);
  };

  const filteredDistributionsFromSupplier = useMemo(() => {
    if (!user || !distributions) return [];
    let result = distributions;
    if (!user.internal) {
      const suppliersFromUser = user.suppliers.flatMap((supplier) => [
        supplier.vendor,
        t('supplier-name-format', { name: supplier.name, vendor: supplier.vendor })
      ]);
      result = distributions.filter((distribution) =>
        suppliersFromUser.includes(distribution.language.supplier)
      );
    }
    return result.filter((distribution) => distribution.status !== DistributionStatus.DRAFT);
  }, [user, distributions]);

  const userHasDistributions =
    status === Status.COMPLETED && filteredDistributionsFromSupplier.length > 0;
  const isInternalUserAndQueryIsAnswered = user?.internal && status === Status.ANSWERED;
  const canCompleteQueryByStatus =
    isInternalUserAndQueryIsAnswered || status === Status.DISTRIBUTED;

  return canCompleteQueryByStatus || userHasDistributions ? (
    <>
      <Button
        className={[classes.button, 'button-bg-white'].join(' ')}
        data-testid="display-query-complete-query"
        data-help-id="display-query-complete-query"
        id="complete-query-button"
        onClick={() => setIsConfirmationBoxOpen(true)}
        design={ButtonDesign.Positive}
      >
        {t('complete-query-button')}
      </Button>
      <CompleteQueryStrategy
        id={id}
        user={user}
        closeConfirmationDialog={() => setIsConfirmationBoxOpen(false)}
        open={isConfirmationBoxOpen}
        status={status}
        distributions={filteredDistributionsFromSupplier}
        setResultMessage={setUserMessage}
      />
      <UserMessage
        isVisible={isUserMessageOpen}
        resultMessage={resultMessage}
        onResetUserMessage={() => setIsUserMessageOpen(false)}
      />
    </>
  ) : null;
};
